import { type FilterState, type SortKey } from "@/hooks/useMediaQuery";
import { movieGenres, regions, sortOptions, tvGenres, varietyGenres, yearOptions } from "@/mocks/filters";

type Props = {
  type: string;
  filter: FilterState;
  onChange: (next: FilterState) => void;
  resultCount: number;
};

function Group({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="border-b border-background-200 py-4 last:border-b-0">
      <h3 className="mb-3 flex items-center justify-between font-label text-[11px] uppercase tracking-[0.16em] text-foreground-500">
        {label}
        {selected.length > 0 ? (
          <span className="rounded-full bg-accent-100 px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-accent-700">
            已选 {selected.length}
          </span>
        ) : null}
      </h3>
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => onToggle("__all__")}
          className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-[12px] transition-colors ${
            selected.length === 0
              ? "bg-primary-500 font-medium text-background-50"
              : "bg-background-100 text-foreground-600 hover:bg-background-200"
          }`}
        >
          全部
        </button>
        {options.map((option) => {
          const active = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onToggle(option)}
              className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-[12px] transition-colors ${
                active
                  ? "bg-primary-500 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterPanel({ type, filter, onChange, resultCount }: Props) {
  const genres = type === "movie" ? movieGenres : type === "tv" ? tvGenres : varietyGenres;

  const toggleValue = (key: "genres" | "region" | "year", value: string) => {
    if (value === "__all__") {
      onChange({ ...filter, [key]: [] });
      return;
    }
    const list = filter[key];
    const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
    onChange({ ...filter, [key]: next });
  };

  const reset = () => onChange({ genres: [], region: [], year: [], sort: filter.sort });

  const hasFilter = filter.genres.length + filter.region.length + filter.year.length > 0;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-4">
      <div className="flex items-center justify-between border-b border-background-200 pb-3">
        <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
          <i className="ri-filter-3-line flex h-4 w-4 items-center justify-center text-accent-600" />
          筛选条件
        </h2>
        <button
          type="button"
          onClick={reset}
          className="cursor-pointer whitespace-nowrap text-[11px] text-foreground-500 transition-colors hover:text-accent-600"
        >
          重置
        </button>
      </div>

      <Group
        label="类型"
        options={genres}
        selected={filter.genres}
        onToggle={(value) => toggleValue("genres", value)}
      />
      <Group
        label="地区"
        options={regions}
        selected={filter.region}
        onToggle={(value) => toggleValue("region", value)}
      />
      <Group
        label="年份"
        options={yearOptions}
        selected={filter.year}
        onToggle={(value) => toggleValue("year", value)}
      />

      <div className="border-b border-background-200 py-4">
        <h3 className="mb-3 font-label text-[11px] uppercase tracking-[0.16em] text-foreground-500">排序</h3>
        <div className="flex flex-col gap-1.5">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange({ ...filter, sort: option.key as SortKey })}
              className={`flex cursor-pointer items-center justify-between rounded-md px-2.5 py-1.5 text-[12px] transition-colors ${
                filter.sort === option.key
                  ? "bg-primary-50 font-medium text-primary-700"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              {option.label}
              {filter.sort === option.key ? (
                <i className="ri-check-line flex h-3.5 w-3.5 items-center justify-center text-accent-600" />
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <div className="pt-4">
        <div className="rounded-md bg-accent-50 px-3 py-2.5">
          <p className="font-label text-[11px] text-accent-800">
            当前筛选结果
            <span className="ml-1 font-heading text-sm font-bold text-accent-700">{resultCount}</span>
            <span className="ml-0.5">部</span>
          </p>
          <p className="mt-1 text-[10px] text-accent-700/70">
            {hasFilter ? "已应用筛选条件，可随时重置" : "尚未应用筛选，显示全部内容"}
          </p>
        </div>
      </div>
    </div>
  );
}